import { CellModel, ColumnSource, Grid, renderers, RowSource } from "@omegagrid/grid";
import rp, { DEFAULT_GRID_EDITOR_OPTIONS, GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { EntityAccess } from "@reporting/rp-types/users";
import { msg } from "@omegagrid/localize";
import { FormList } from "@omegagrid/form";
import { EntityAccessType, DEFAULT_ENTITY_ACCESS_CHANGE } from "@reporting/rp-types/users";
import { UserTreeAdapter } from "./userTreeAdapter";
import { EntityAccessExt } from "../types";

// const BG_RED = ['#a13f3f', '#ffffff'];
// const BG_GREEN = ['#00955d', '#ffffff'];
const COLOR_GREEN = '#008000';
const COLOR_RED = '#c30000ff';

export class EntityUsersGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];
    private _entityTypes: {entity: string, description: string}[];

	private _defaultAccess = false;
	private _origDefaultAccess = false;
	get defaultAccess() { return this._defaultAccess }
	set defaultAccess(v: boolean) {
		this._defaultAccess = v;
		this._origDefaultAccess = v;
	}

	constructor(
		private entity: EntityAccessType,
		private id: string
	) { super() }

    afterLoad(): void {
        this._rows = null;
    }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 1,
			content: [{
				action: 'delete',
				icon: 'trash',
				color: 'red'
			}],
			filter: false
		}, {
			data: 'subject',
			header: `${msg('User')}/${msg('Role')}`,
			t: 's',
            adapter: new UserTreeAdapter(true, { search: true, selectMode: 'single', allowEmpty: false }),
			e: false,
			w: 250,
			renderer: (div: HTMLDivElement, cell: CellModel) => {
				if (!cell.row.data) {
					div = renderers.DropdownRenderer(div, cell);
				} else if (cell.row.data['user_name']) {
					div.innerHTML = `<og-icon icon="user"></og-icon> ${cell.row.data['login_name'] ?? cell.row.data['user_name']}`;
				} else if (cell.row.data['id_access_role']) {
					div.innerHTML = `<og-icon icon="users"></og-icon> ${cell.row.data['role']}`;
				} else {
					div = renderers.DropdownRenderer(div, cell);
				}
				return renderers.BaseRenderer(div, cell);
			}
		}, {
			data: 'allow',
			header: msg('Allowed'),
			t: 'b',
			e: true,
			w: 100
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 180
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true,
		}];
		return this._columns;
	}

	async loadRows() {
		if (this._rows) return this._rows;
        let entities = await rp.ms('users').get<EntityAccess[]>(`/entityAccess/entityUsers`, {
            entity: this.entity,
			id: this.id,
        });

        entities = entities.filter(entity => {
            if (entity.id_access_role || entity.user_name) {
                return true;
            } else {
                this.defaultAccess = !!entity.allow;
                return false;
            }
        });

        return entities.map<RowSource>(data => ({ data: {
            ...data,
            subject: data.user_name ? `u${data.user_name}` : data.id_access_role ? `r${data.id_access_role}` : null,
        }}));
	}

	async getRows(): Promise<RowSource[]>{
		return await this.loadRows();
	}

	async save() {
        let error = false;
        const rowData = this.editor.getRowData(false);
		const data = rowData.map(row => {
            if (row.state === 'new' && row.data['subject'] === undefined) {
                rp.host.bucket.showAlert({content: msg('Subject cannot be empty!'), type: 'warning', title: msg('Validation Error')});
                error = true;
                return;
            }
            const subject = String(row.data['subject']);
			return {
				_state: row.state,
                idz: row.data['idz'],
				entity: this.entity,
				id: this.id,
				user_name: subject && subject.startsWith('u') ? subject.substring(1) : null,
				id_access_role: subject && subject.startsWith('r') ? parseInt(subject.substring(1)) : null,
				allow: row.data['allow'] ? true : false,
			} as EntityAccessExt;
		});

        const defaultAccess = this.editor.customToolbar.values.defaultAccess === undefined
            ? this.defaultAccess
            : this.editor.customToolbar.values.defaultAccess == 'allowed';
        if (defaultAccess != this._origDefaultAccess) {
            data.push({
                _state: 'edited',
                entity: this.entity,
                id: this.id,
                user_name: null,
                id_access_role: null,
                allow: defaultAccess,
            } as EntityAccessExt);
        }

        if (error) return Promise.reject('Validation error');
        if (data.length > 0) {
            await rp.ms('users').post(`entityAccess`, data);
            this.editor.refresh();
        }
	}

	onAction(action: string, cell: CellModel) {
		const grid = this.component as Grid;
		switch (action) {
			case 'defaultAccess':
                this._defaultAccess = this.editor.customToolbar.values.defaultAccess == 'allowed';
				grid.rendering.render();
                this.editor.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
				break;
			case 'addRow':
				cell.row.cell(1).editable = true;
				break;
			case 'discard':
				if (this._defaultAccess != this._origDefaultAccess) {
					this._defaultAccess = this._origDefaultAccess;
					const list = this.editor.customToolbar.controls.get('defaultAccess') as FormList;
					list.value = this._defaultAccess ? 'allowed' : 'denied';
					grid.rendering.render();
				}
				break;
		}
	}

    async getEntityDescription(): Promise<string> {
        if (!this._entityTypes) {
            this._entityTypes = await rp.ms('users').get<{entity: string, description: string}[]>('/entityAccess/userTypes');
        }
        const et = this._entityTypes.find(et => et.entity === this.entity);
        return et ? msg(et.description) : this.entity;
    }

    async getOptions(): Promise<Partial<GridEditorOptions>> {
		this._rows = await this.loadRows();
		const defaultAccessChange = DEFAULT_ENTITY_ACCESS_CHANGE[this.entity] ?? false;
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: null,
			refreshAfterSave: false,
			saveMode: 'custom',
			customToolbar: [{
				type: 'dropdown',
				id: 'defaultAccess',
				text: msg('Default access'),
				width: 150,
				disabled: !defaultAccessChange,
				value: this.defaultAccess ? 'allowed' : 'denied',
				items: [
					{ k: 'allowed', v: `${msg('Default')}: ${msg('Allowed')}` },
					{ k: 'denied', v: `${msg('Default')}: ${msg('Denied')}` }
				],
			}, {
                type: 'label',
				id: 'entityType',
				gap: 10,
				value: await this.getEntityDescription(),
			}],
			afterRenderer: (div: HTMLDivElement, cell: CellModel) => {
				if (cell.column.index !== 1 || !cell.row.data) {
                    return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
				}

                const allowCell = cell.row.cell(2);
				if (allowCell.value && !this.defaultAccess) {
					div.style.color = COLOR_GREEN;
				} else if (!allowCell.value && this.defaultAccess) {
					div.style.color = COLOR_RED;
				}

				return DEFAULT_GRID_EDITOR_OPTIONS.afterRenderer(div, cell);
			}
		});
	}

}
